/*  ========================================================================
    # Component - PageLinksSlider
    ========================================================================  */

import React, { useEffect, useState } from 'react';
import tailwindConfig from '../../../../tailwind.config';
import PropTypes from 'prop-types';
import LazyImage from '../../components/LazyImage';
import LazySource from '../../components/LazySource';
import { useSwipeable } from 'react-swipeable';
import useSVGSprite from '../../hooks/useSVGSprite';
import useTranslation from '../../hooks/useTranslation';
import useBreakpoints from '../../hooks/useBreakpoints';

const CardTitle = props => {
  const { as: Tag = 'h4', children } = props;
  return <Tag className='s-page-links-slider__slide-title h4'>{children}</Tag>;
};

CardTitle.propTypes = {
  as: PropTypes.string,
  children: PropTypes.node
};

const PageLinksSlider = ({ heading, semanticHeadlineLevel, entries }) => {
  const __ = useTranslation();
  const slidesRef = React.useRef(null);
  const breakpoints = useBreakpoints();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Icons
  const sprite = useSVGSprite();
  const arrowLeft = `${sprite}#arrow-left`;

  // Cursor position handlers
  const updateCursorPosition = (slideElement, e) => {
    // Get the position relative to the slide element
    const rect = slideElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Force a small delay to ensure smooth positioning
    requestAnimationFrame(() => {
      slideElement.style.setProperty('--cursor-x', `${x}px`);
      slideElement.style.setProperty('--cursor-y', `${y}px`);
    });
  };

  const handleMouseEnter = e => {
    const slide = e.currentTarget;
    updateCursorPosition(slide, e);
  };

  const handleMouseMove = e => {
    const slide = e.currentTarget;
    updateCursorPosition(slide, e);
  };

  const calcSlideWidth = () => {
    if (breakpoints.xl) return 640;
    else if (breakpoints.md) return 480;
    else return 320;
  };

  const checkScrollRight = (scrolledRight = false, scrolledLeft = false) => {
    if (!slidesRef.current) return false;
    let currentScrollLeft = slidesRef.current.scrollLeft;
    const slideWidth = calcSlideWidth();
    if (scrolledRight) {
      currentScrollLeft += slideWidth;
    }
    if (scrolledLeft) {
      currentScrollLeft -= slideWidth;
    }
    const maxScrollLeft = slidesRef.current.scrollWidth - slidesRef.current.clientWidth + slideWidth;
    setCanScrollRight(currentScrollLeft + slideWidth < maxScrollLeft);
  };

  const checkScrollLeft = (scrolledRight = false, scrolledLeft = false) => {
    if (!slidesRef.current) return false;
    let currentScrollLeft = slidesRef.current.scrollLeft;
    const slideWidth = calcSlideWidth();
    if (scrolledRight) {
      currentScrollLeft += slideWidth;
    }
    if (scrolledLeft) {
      currentScrollLeft -= slideWidth;
    }

    setCanScrollLeft(currentScrollLeft - slideWidth >= 0);
  };

  const scrollRight = () => {
    if (!slidesRef.current) return;
    const currentScrollLeft = slidesRef.current.scrollLeft;
    const slideWidth = calcSlideWidth();
    slidesRef.current.scrollLeft = currentScrollLeft + slideWidth;
    checkScrollLeft(true, false);
    checkScrollRight(true, false);
  };

  const scrollLeft = () => {
    if (!slidesRef.current) return;
    const currentScrollLeft = slidesRef.current.scrollLeft;
    const slideWidth = calcSlideWidth();
    slidesRef.current.scrollLeft = currentScrollLeft - slideWidth;
    checkScrollLeft(false, true);
    checkScrollRight(false, true);
  };

  // Navigate Left
  const navigateLeft = () => {
    if (canScrollLeft) {
      scrollLeft();
    }
  };

  // Navigate Right
  const navigateRight = () => {
    if (canScrollRight) {
      scrollRight();
    }
  };

  // Add swipe handlers.
  const swipeHandlers = useSwipeable({
    onSwipedRight: navigateLeft,
    onSwipedLeft: navigateRight
  });

  useEffect(() => {
    const scrollContainer = slidesRef.current;
    if (!scrollContainer) return;

    let scrollTimeout;

    const handleScroll = () => {
      // Clear the timeout if it exists
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Set a new timeout to update scroll state after scrolling stops
      scrollTimeout = setTimeout(() => {
        checkScrollLeft();
        checkScrollRight();
      }, 150);
    };

    // Add the scroll event listener
    scrollContainer.addEventListener('scroll', handleScroll);

    // Initial check
    checkScrollLeft();
    checkScrollRight();

    // Cleanup function
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  return (
    <>
      <div className='container'>
        <div className='s-page-links-slider__top'>
          <div className='s-page-links-slider__heading wysiwyg-block' dangerouslySetInnerHTML={{ __html: heading }}></div>
          <div className='s-page-links-slider__navigation'>
            <button
              className='s-page-links-slider__navigation-button s-page-links-slider__navigation-button--left'
              aria-label={__('pages-links-slider-navigate-left')}
              onClick={navigateLeft}
              disabled={!canScrollLeft}
              type='button'
            >
              <svg className='s-page-links-slider__navigation-icon' aria-hidden='true'>
                <use href={arrowLeft}></use>
              </svg>
            </button>
            <button
              className='s-page-links-slider__navigation-button s-page-links-slider__navigation-button--right'
              aria-label={__('pages-links-slider-navigate-right')}
              onClick={navigateRight}
              disabled={!canScrollRight}
              type='button'
            >
              <svg className='s-page-links-slider__navigation-icon' aria-hidden='true'>
                <use href={arrowLeft}></use>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div
        className='s-page-links-slider__slides'
        {...swipeHandlers}
        ref={slidesRef}
      >
        {entries.map((entry, index) => (
          <a
            href={entry.page.permalink}
            className={`s-page-links-slider__slide s-page-links-slider__slide--${entry.page.colorScheme}`}
            target='_self'
            key={`${entry.page.post_title}_${index}`}
            rel='noreferrer'
            aria-label={entry.page.post_title}
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
          >
            <LazyImage
              figureClass='s-page-links-slider__slide-image'
              defaultUrl={entry.page.featured_image.sizes.large}
              alt={entry.page.featured_image.alt}
              placehoderActive={false}
            >
              <LazySource
                srcSet={entry.page.featured_image.sizes.large}
                mediaMin={tailwindConfig.theme.screens.md}
              />
              <LazySource
                srcSet={entry.page.featured_image.sizes.medium_large}
                mediaMin={tailwindConfig.theme.screens.sm}
              />
            </LazyImage>
            <CardTitle as={semanticHeadlineLevel}>{entry.page.post_title}</CardTitle>
            <div className='s-page-links-slider__slide-excerpt' dangerouslySetInnerHTML={{ __html: entry.alternate_excerpt ? entry.alternate_excerpt : entry.page.excerpt }}></div>
          </a>
        ))}
      </div>
    </>
  );
};

PageLinksSlider.propTypes = {
  heading: PropTypes.string,
  semanticHeadlineLevel: PropTypes.string,
  entries: PropTypes.array
};

export default PageLinksSlider;
